import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Search, X, IndianRupee, Phone, Mail, Building2, Tag, Sparkles,
  Loader2, Trash2, ChevronRight, MessageSquare, PhoneCall, CalendarPlus,
  CheckCircle2, Briefcase,
} from "lucide-react";

const STAGE_COLORS = {
  new: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  qualified: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  proposal: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  negotiation: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  won: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  lost: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

const fmtINR = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);

export default function CRM() {
  const [pipeline, setPipeline] = useState(null);
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [stages, setStages] = useState([]);

  const load = async () => {
    const { data } = await api.get("/crm/pipeline");
    setPipeline(data);
    const { data: s } = await api.get("/crm/stages");
    setStages(s);
  };

  useEffect(() => { load(); }, []);

  const updateStage = async (lead, newStage) => {
    try {
      await api.patch(`/crm/leads/${lead.id}`, { stage: newStage });
      toast.success(`Moved ${lead.name} → ${newStage}`);
      await load();
    } catch { toast.error("Move failed"); }
  };

  if (!pipeline) {
    return <div className="h-64 glass rounded-2xl animate-pulse" />;
  }

  const filteredStages = pipeline.stages.map((s) => ({
    ...s,
    leads: s.leads.filter((l) =>
      !search || l.name.toLowerCase().includes(search.toLowerCase()) ||
      (l.company || "").toLowerCase().includes(search.toLowerCase()) ||
      (l.email || "").toLowerCase().includes(search.toLowerCase())
    ),
  }));

  return (
    <div className="space-y-6" data-testid="crm-root">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
            <Briefcase className="h-3.5 w-3.5" /> AI CRM
          </div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Pipeline</h1>
          <p className="text-white/50 mt-2 max-w-2xl text-sm">
            Track leads through the funnel. Drag-equivalent: click a stage chip to move the lead. Get AI next-step suggestions for any open lead.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              if (!window.confirm("Generate AI next-step suggestions for ALL open leads? Uses LLM credits.")) return;
              try {
                const { data } = await api.post("/crm/ai-next-step/bulk");
                toast.success(`AI suggested next steps for ${data.processed} lead${data.processed === 1 ? "" : "s"}`);
                await load();
              } catch { toast.error("Bulk AI failed"); }
            }}
            data-testid="crm-bulk-ai-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg glass text-sm font-semibold hover:bg-white/5"
          >
            <Sparkles className="h-4 w-4 text-indigo-300" /> AI bulk-suggest
          </button>
          <button
            onClick={() => setShowNew(true)}
            data-testid="crm-new-lead-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> New lead
          </button>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="crm-summary">
        <SummaryCard label="Total leads" value={pipeline.summary.total_leads} />
        <SummaryCard label="Open value" value={fmtINR(pipeline.summary.open_value)} />
        <SummaryCard label="Won" value={pipeline.summary.won_count} />
        <SummaryCard label="Won value" value={fmtINR(pipeline.summary.won_value)} accent="emerald" />
      </section>

      <div className="glass rounded-xl p-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-white/40" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, company..."
          className="bg-transparent flex-1 text-sm outline-none"
          data-testid="crm-search"
        />
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 overflow-x-auto" data-testid="crm-kanban">
        {filteredStages.map((s) => (
          <div key={s.id} className="min-w-[200px]">
            <div className={`text-xs uppercase tracking-[0.2em] px-3 py-2 rounded-t-lg border ${STAGE_COLORS[s.id]}`}>
              {s.label} · <span className="font-mono">{s.count}</span>
            </div>
            <div className="space-y-2 mt-2">
              {s.leads.length === 0 && (
                <div className="text-xs text-white/30 text-center py-6 border border-dashed border-white/10 rounded-lg">
                  empty
                </div>
              )}
              {s.leads.map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  stages={stages}
                  onMove={(ns) => updateStage(lead, ns)}
                  onClick={() => setSelected(lead)}
                />
              ))}
            </div>
          </div>
        ))}
      </section>

      {showNew && (
        <NewLeadModal stages={stages} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      )}
      {selected && (
        <LeadDrawer lead={selected} onClose={() => setSelected(null)} onUpdated={load} stages={stages} />
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</div>
      <div className={`font-heading text-2xl font-black mt-1 truncate ${accent === "emerald" ? "text-emerald-300" : ""}`}>{value}</div>
    </div>
  );
}

function LeadCard({ lead, stages, onMove, onClick }) {
  return (
    <div className="glass rounded-lg p-3 hover:bg-white/[0.04] cursor-pointer" onClick={onClick} data-testid={`crm-lead-${lead.id}`}>
      <div className="font-semibold text-sm truncate">{lead.name}</div>
      {lead.company && <div className="text-xs text-white/40 truncate flex items-center gap-1"><Building2 className="h-3 w-3" />{lead.company}</div>}
      {lead.deal_value > 0 && (
        <div className="text-xs text-emerald-300 mt-1 font-mono">{fmtINR(lead.deal_value)}</div>
      )}
      <select
        value={lead.stage}
        onChange={(e) => { e.stopPropagation(); onMove(e.target.value); }}
        onClick={(e) => e.stopPropagation()}
        className="mt-2 w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-[10px]"
        data-testid={`crm-lead-stage-${lead.id}`}
      >
        {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
    </div>
  );
}

function NewLeadModal({ stages, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", deal_value: 0, stage: "new", notes: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post("/crm/leads", form);
      toast.success("Lead created");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="glass rounded-2xl p-6 max-w-lg w-full border border-indigo-500/30" onClick={(e) => e.stopPropagation()} data-testid="crm-new-lead-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl font-black">New lead</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-white/40" /></button>
        </div>
        <div className="space-y-3">
          {["name", "email", "phone", "company"].map((f) => (
            <input key={f} value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              placeholder={f.charAt(0).toUpperCase() + f.slice(1)} data-testid={`crm-form-${f}`}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          ))}
          <input type="number" value={form.deal_value} onChange={(e) => setForm({ ...form, deal_value: parseFloat(e.target.value) || 0 })}
            placeholder="Deal value (₹)" data-testid="crm-form-value"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
            data-testid="crm-form-stage" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
            {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Notes" data-testid="crm-form-notes"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        </div>
        <button onClick={save} disabled={saving} data-testid="crm-form-save"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create lead
        </button>
      </div>
    </div>
  );
}

function LeadDrawer({ lead, onClose, onUpdated, stages }) {
  const [full, setFull] = useState(null);
  const [aiStep, setAiStep] = useState(lead.ai_next_step || null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [actType, setActType] = useState("note");
  const [actContent, setActContent] = useState("");

  const load = async () => {
    const { data } = await api.get(`/crm/leads/${lead.id}`);
    setFull(data);
    setAiStep(data.ai_next_step || null);
  };
  useEffect(() => { load(); }, [lead.id]);

  const aiNext = async () => {
    setLoadingAi(true);
    try {
      const { data } = await api.post(`/crm/leads/${lead.id}/ai-next-step`);
      setAiStep(data.suggestion);
    } catch { toast.error("AI failed"); }
    finally { setLoadingAi(false); }
  };

  const addActivity = async () => {
    if (!actContent.trim()) return;
    try {
      await api.post(`/crm/leads/${lead.id}/activities`, { type: actType, content: actContent });
      setActContent("");
      await load();
      onUpdated();
    } catch { toast.error("Add failed"); }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${lead.name}?`)) return;
    await api.delete(`/crm/leads/${lead.id}`);
    toast.success("Deleted");
    onClose();
    onUpdated();
  };

  const updateField = async (field, value) => {
    await api.patch(`/crm/leads/${lead.id}`, { [field]: value });
    await load();
    onUpdated();
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-[#0a0e1a] border-l border-white/10 w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="crm-lead-drawer">
        <div className="p-6 sticky top-0 bg-[#0a0e1a]/95 backdrop-blur border-b border-white/10 z-10 flex items-center justify-between">
          <div className="min-w-0">
            <div className="font-heading text-xl font-black truncate">{lead.name}</div>
            {lead.company && <div className="text-xs text-white/40">{lead.company}</div>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={del} data-testid="crm-delete-btn" className="p-2 rounded hover:bg-rose-500/10"><Trash2 className="h-4 w-4 text-rose-300" /></button>
            <button onClick={onClose}><X className="h-5 w-5 text-white/60" /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-2 text-xs">
            {lead.email && <a href={`mailto:${lead.email}`} className="glass rounded-lg p-2 inline-flex items-center gap-2"><Mail className="h-3 w-3" />{lead.email}</a>}
            {lead.phone && <a href={`tel:${lead.phone}`} className="glass rounded-lg p-2 inline-flex items-center gap-2"><Phone className="h-3 w-3" />{lead.phone}</a>}
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Stage</div>
            <select value={full?.stage || lead.stage} onChange={(e) => updateField("stage", e.target.value)}
              data-testid="crm-drawer-stage" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
              {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2 flex items-center gap-2">
              <Sparkles className="h-3 w-3 text-indigo-400" /> AI next step
            </div>
            {aiStep ? (
              <div className="glass rounded-lg p-3 text-sm whitespace-pre-wrap border border-indigo-500/30">{aiStep}</div>
            ) : (
              <div className="glass rounded-lg p-3 text-sm text-white/40">Ask AI what to do next.</div>
            )}
            <button onClick={aiNext} disabled={loadingAi} data-testid="crm-ai-next-btn"
              className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg brand-gradient text-xs font-semibold hover:opacity-90 disabled:opacity-50">
              {loadingAi ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} {aiStep ? "Regenerate" : "Ask AI"}
            </button>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Add activity</div>
            <div className="flex gap-2 mb-2 text-xs">
              {["note", "call", "email", "meeting", "task"].map((t) => (
                <button key={t} onClick={() => setActType(t)} data-testid={`crm-act-type-${t}`}
                  className={`px-2 py-1 rounded ${actType === t ? "brand-gradient" : "glass"}`}>{t}</button>
              ))}
            </div>
            <textarea rows={2} value={actContent} onChange={(e) => setActContent(e.target.value)}
              data-testid="crm-act-content"
              placeholder="What happened?" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
            <button onClick={addActivity} data-testid="crm-act-add-btn"
              className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg glass text-xs font-semibold hover:bg-white/5">
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>

          {full?.activities?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Activity timeline</div>
              <div className="space-y-2">
                {full.activities.map((a) => (
                  <div key={a.id} className="glass rounded-lg p-3 text-xs" data-testid={`crm-activity-${a.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="uppercase tracking-[0.2em] text-indigo-300">{a.type}</span>
                      <span className="text-white/30 font-mono">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-1 text-white/80">{a.content}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
